import ReceivedAtDate from '../../../../../components/ReceivedAtDate';
import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';

import { TIME_FORMAT_12_UPPERCASE, DATE_MONTH_DAY_YEAR } from '../../../../../constants';
import Table3, { columnHelper } from '../../../../../containers/Table/Table3';
import { actions, PLOT_DATA_RESULT } from '../Graph/actions';
import Strings from '../../../../../Strings';
import { getCapChargeText } from '../../../../../utils';
import { useGlobalPageSize } from '../../../../../utils/tableUtils';

function BatteryConnectionTable(props) {
  const [isLoading, setIsLoading] = React.useState(true);
  const [dataToShow, setDataToShow] = React.useState([]);
  const [pagination, setPagination] = React.useState({});

  const globalPageSize = useGlobalPageSize();
  const pageRequest = React.useRef({
    offset: 0,
    filterBy: props.eventType,
    startDate: props.startDate,
    endDate: props.endDate,
    deviceId: props.deviceId,
    hubId: props.hubId,
    kitId: props.kitId,
    patientId: props.patientId,
    dataType: props.eventType,
    limit: globalPageSize,
  });

  const getEvents = React.useCallback(() => {
    setIsLoading(true);
    props.getEvents(pageRequest.current).then(resp => {
      if (resp.type === PLOT_DATA_RESULT) {
        setDataToShow(resp.response.data);
        setPagination({...resp.response.pagination, limit: pageRequest.current.limit }); // workaround for backend not returning limit correctly
      }
      setIsLoading(false);
    });
  }, []);

  React.useEffect(() => {
    getEvents();
  }, []);

  React.useEffect(() => {
    pageRequest.current.startDate = props.startDate;
    pageRequest.current.endDate = props.endDate;
    pageRequest.current.filterBy = props.eventType;
    pageRequest.current.deviceId = props.deviceId;
    pageRequest.current.hubId = props.hubId;
    pageRequest.current.kitId = props.kitId;
    pageRequest.current.patientId = props.patientId;
    pageRequest.current.dataType = props.eventType;
    pageRequest.current.offset = 0;
    getEvents();
  }, [props.deviceId, props.startDate, props.endDate, props.hubId, props.kitId, props.patientId, props.eventType]);

  const onOffsetChange = (offset, limit) => {
    pageRequest.current.offset = offset;
    if (limit) {
      pageRequest.current.limit = limit;
    }
    getEvents();
  };

  const onPageSizeChange = React.useCallback((newSize) => {
    pageRequest.current.offset = 0;
    pageRequest.current.limit = newSize;
    getEvents();
  }, []);

  const columns = React.useMemo(() => {
    const cols = [
      columnHelper.accessor('date', {
        accessorFn: row => row.event_timestamp,
        header: Strings.date,
        cell: info => (
          <ReceivedAtDate
            measuredAt={info.getValue()}
            backendTimestamp={info.row.original.backend_timestamp}
            displayFormat={`${DATE_MONTH_DAY_YEAR} ${TIME_FORMAT_12_UPPERCASE}`}
          >
            {info.getValue() ? moment(info.getValue()).format(DATE_MONTH_DAY_YEAR) : '-'}
          </ReceivedAtDate>
        ),
        size: 200,
      }),
      columnHelper.accessor('time', {
        accessorFn: row => row.event_timestamp,
        header: Strings.time,
        cell: info => (info.getValue() ? moment(info.getValue()).format(TIME_FORMAT_12_UPPERCASE) : '-'),
        size: 200,
      }),
    ];

    if (props.eventType === 'battery') {
      cols.push(
        columnHelper.accessor('battery_percentage', {
          header: Strings.battery.battery,
          cell: info => getCapChargeText(info.getValue()),
          size: 100,
        }),
      );
    }

    return cols;
  }, [props.eventType]);

  return (
    <Table3
      name="battery-connection-table"
      data={dataToShow}
      columns={columns}
      pagination={pagination}
      isLoading={isLoading}
      onOffsetChange={onOffsetChange}
      onPageSizeChange={onPageSizeChange}
    />
  );
}

BatteryConnectionTable.propTypes = {
  eventType: PropTypes.string.isRequired,
  deviceId: PropTypes.string.isRequired,
  hubId: PropTypes.string,
  kitId: PropTypes.string,
  patientId: PropTypes.string,
  startDate: PropTypes.string.isRequired,
  endDate: PropTypes.string.isRequired,
  getEvents: PropTypes.func.isRequired,
};

const mapDispatchToProps = dispatch => ({
  getEvents: pageRequest => dispatch(actions.getPlotData(pageRequest)),
});

export default connect(null, mapDispatchToProps)(BatteryConnectionTable);
